import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import throttle from 'lodash/throttle';
import format from 'date-fns/format';
import compareAsc from 'date-fns/compareAsc';
import isBefore from 'date-fns/isBefore';
import isValid from 'date-fns/isValid';
import differenceInCalendarYears from 'date-fns/differenceInCalendarYears';
import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import startOfYear from 'date-fns/startOfYear';
import startOfMonth from 'date-fns/startOfMonth';
import addYears from 'date-fns/addYears';
import addMonths from 'date-fns/addMonths';
import addDays from 'date-fns/addDays';
import isEqual from 'date-fns/isEqual';
import closestTo from 'date-fns/closestTo';
// import closestIndexTo from 'date-fns/closestIndexTo';
import isAfter from 'date-fns/isAfter';
import { handleControllerNavigation } from '../../actions/timelineTitleActions';
import Draggable from 'react-draggable';
import ControllerLoupe from './ControllerLoupe';


class Controller extends Component {
  constructor (props) {
    super(props);
    this.state = {
      bigInterval: null,
      smallInterval: null,
      scaleWidth: null,
      startDate: null,
      endDate: null,
      scaleStartDate: null,
      scaleEndDate: null,
      barPosition: null,
      barLeftOffset: 400,
      scrollHintVisible: true,
      scaleMultipliers: {},
      intervalMarkers: [],
    };
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = throttle(this.handleDrag.bind(this), 50);
    this.handleDragStop = this.handleDragStop.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
    this.setupScale = this.setupScale.bind(this);
    this.setupAxis = this.setupAxis.bind(this);
  }

  UNSAFE_componentWillMount () { // eslint-disable-line camelcase
    const { containerWidth, activeSlideIndex } = this.props;
    this.setupScale(this.setupAxis.bind(this, () => this.handleSlideChange(activeSlideIndex)));
    this.setState({ barPosition: { x: containerWidth / 2, y: 0 } });
  }

  componentDidMount () {
    window.addEventListener('resize', this.handleWindowResize);
  }

  componentDidUpdate (prevProps, prevState) {
    const { activeEra, activeSlideIndex, containerWidth } = this.props;
    const { barPosition: { x } } = this.state;
    if (activeEra !== prevProps.activeEra) {
      this.setupScale(this.setupAxis.bind(this, () => this.handleSlideChange(activeSlideIndex)));
    }
    if (activeSlideIndex !== prevProps.activeSlideIndex) {
      this.handleSlideChange(activeSlideIndex);
    }
    if (containerWidth !== prevProps.containerWidth) {
      const delta = containerWidth - prevProps.containerWidth;
      this.setState({ barPosition: { x: x + delta / 2, y: 0 } });
    }
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  handleDragStart (e, data) {
    this.setState({ scrollHintVisible: false });
  }

  handleDrag (e, data) {
    this.setState({ barPosition: { x: data.x, y: 0 } });
  }

  handleDragStop (e, data) {
    const { actions, titleId, containerWidth } = this.props;
    const { intervalMarkers, barLeftOffset } = this.state;
    if (intervalMarkers.length === 0) {
      return null;
    }
    const { x } = data;
    const barCenter = containerWidth / 2 - x - barLeftOffset;
    const closest = intervalMarkers.reduce((prev, cur) => {
      return (Math.abs(cur.left - barCenter) < Math.abs(prev.left - barCenter) ? cur : prev);
    });
    actions.handleControllerNavigation(titleId, closest.date);
    this.setState({ scrollHintVisible: true });
  }

  handleSlideChange (index) {
    const { containerWidth, slideData: { slides } } = this.props;
    const { intervalMarkers, barLeftOffset } = this.state;
    const slideDate = slides[index].date;
    if (!isValid(slideDate)) {
      return;
    }
    // const markerIndex = closestIndexTo(slideDate, intervalMarkers.map(i => i.date));

    // Snap to the last marker *prior to* the active slide date, so date displayed in control
    // bar aligns with the date of the slide.
    let markerIndex = intervalMarkers.findIndex(i => isAfter(i.date, slideDate));
    markerIndex = markerIndex > 0 ? markerIndex - 1 : 0;
    const left = intervalMarkers[markerIndex].left;
    const x = containerWidth / 2 - left - barLeftOffset;
    this.setState({
      transitioning: true,
      barPosition: { x, y: 0 },
    }, () => {
      setTimeout(() => {
        this.setState({ transitioning: false });
      }, 300);
    });
  }

  setupScale (cb = () => null) {
    const { baseIntervalWidth, slideData: { slides } } = this.props;
    const [{ date: endDate }] = slides.slice(-1);
    const [{ date: startDate }] = slides;
    const yearDelta = differenceInCalendarYears(endDate, startDate);
    const monthDelta = differenceInCalendarMonths(endDate, startDate);
    const dayDelta = differenceInCalendarDays(endDate, startDate);

    let bigInterval, smallInterval;
    let intervalWidth = baseIntervalWidth;

    if (yearDelta <= 1) {
      bigInterval = { scale: 'day', step: 7 };
      smallInterval = { scale: 'day', step: 1 };
    } else if (yearDelta <= 5) {
      bigInterval = { scale: 'month', step: 1 };
      smallInterval = { scale: 'day', step: 7 };
    } else if (yearDelta <= 20) {
      bigInterval = { scale: 'year', step: 1 };
      smallInterval = { scale: 'month', step: 1 };
      intervalWidth *= 1.5;
    } else if (yearDelta <= 50) {
      bigInterval = { scale: 'year', step: 1 };
      smallInterval = { scale: 'month', step: 1 };
    } else if (yearDelta <= 300) {
      bigInterval = { scale: 'year', step: 5 };
      smallInterval = { scale: 'year', step: 1 };
    } else if (yearDelta <= 600) {
      bigInterval = { scale: 'year', step: 10 };
      smallInterval = { scale: 'year', step: 1 };
    } else if (yearDelta <= 1200) {
      bigInterval = { scale: 'year', step: 20 };
      smallInterval = { scale: 'year', step: 2 };
    } else {
      bigInterval = { scale: 'year', step: 50 };
      smallInterval = { scale: 'year', step: 5 };
    }

    let scaleWidth;
    switch (bigInterval.scale) {
      case 'day':
        scaleWidth = (dayDelta / bigInterval.step + 1) * intervalWidth;
        break;
      case 'month':
        scaleWidth = (monthDelta / bigInterval.step + 1) * intervalWidth;
        break;
      case 'year':
        scaleWidth = (yearDelta / bigInterval.step + 1) * intervalWidth;
        break;
      default:
        break;
    }

    this.setState({
      bigInterval,
      smallInterval,
      scaleWidth,
      startDate,
      endDate,
    }, cb);
  }

  setupAxis (cb = () => null) {
    const { bigInterval, smallInterval, scaleWidth, startDate, endDate } = this.state;
    const addTimeFuncs = {
      year: addYears,
      month: addMonths,
      day: addDays,
    };
    const addTimeBig = addTimeFuncs[bigInterval.scale];
    const addTimeSmall = addTimeFuncs[smallInterval.scale];

    const roundYearFunc = (date, step) => {
      if (step === 1 || !step) {
        return startOfYear(date);
      }
      // Start on a year that is a multiple of the step value, so
      // labels display nice round numbers.
      const stepYear = date.getFullYear() - (date.getFullYear() % step);
      return new Date(stepYear, 0);
    };

    const roundDateFuncs = {
      year: roundYearFunc,
      month: date => startOfMonth(date),
      day: date => startOfMonth(date),
    };
    const scaleStartDate = roundDateFuncs[bigInterval.scale](startDate, bigInterval.step);
    const scaleEndDate = roundDateFuncs[bigInterval.scale](addTimeBig(endDate, bigInterval.step));

    const bigIntItems = [];
    let smallIntItems = [];

    // Build array of dates for major interval markers
    let curDate = scaleStartDate;
    while (curDate <= scaleEndDate) {
      bigIntItems.push(curDate);
      curDate = addTimeBig(curDate, bigInterval.step);
    }

    // Build array of dates for minor interval markers
    const [lastBigInt] = bigIntItems.slice(-1);
    curDate = scaleStartDate;
    while (curDate <= scaleEndDate) {
      const closestBigInt = closestTo(curDate, bigIntItems);
      // Exclude items that either overlap with an existing major interval marker
      // or occur after the final major interval marker.
      if (!isEqual(curDate, closestBigInt) && isBefore(curDate, lastBigInt)) {
        smallIntItems.push(curDate);
      }
      curDate = addTimeSmall(curDate, smallInterval.step);
    }

    const formatLabel = (date, long = false) => {
      const formats = {
        day: long ? 'MMM d, yyyy' : 'MMM d',
        month: long ? 'MMM yyyy' : 'MMM',
        year: 'yyyy',
      };
      return format(date, formats[bigInterval.scale]);
    };

    const spacing = scaleWidth / (bigIntItems.length + smallIntItems.length);

    const intItems = [
      ...bigIntItems.map((date, i) => {
        // Ensure long format is displayed when crossing into the next year
        const longFormat = i === 0 ||
          (bigInterval.scale === 'month' && date.getMonth() === 0) ||
          (bigInterval.scale === 'day' && date.getMonth() === 0 && bigIntItems[i - 1].getMonth() !== 0);

        return {
          date,
          intervalType: 'big',
          label: formatLabel(date, longFormat),
        };
      }),
      ...smallIntItems.map(date => ({
        date,
        intervalType: 'small',
      })),
    ].sort((a, b) => compareAsc(a.date, b.date)).map((item, i) => { item.left = i * spacing; return item; });

    const scaleMultipliers = {
      day: scaleWidth / differenceInCalendarDays(scaleEndDate, scaleStartDate),
    };

    this.setState({
      scaleStartDate,
      scaleEndDate,
      scaleMultipliers,
      intervalMarkers: intItems,
    }, cb);
  }

  renderAxis () {
    const { markerLeftOffset } = this.props;
    const { intervalMarkers } = this.state;
    if (intervalMarkers.length === 0) {
      return null;
    }

    return (
      <div className="timeline-controller-axis">
        {intervalMarkers.map(({ date, intervalType, label, left }, i) => {
          const markerStyle = {
            transform: `translateX(${left + markerLeftOffset}px)`,
          };
          return (
            <div key={i} className={`int-${intervalType}`} style={markerStyle}>
              {label
                ? <div className="timeline-controller-label"><span>{label}</span></div>
                : null
              }
            </div>
          );
        })}
      </div>
    );
  }

  renderSlideMarkers () {
    const { slideData: { slides, categoryKey } } = this.props;
    const { intervalMarkers, scaleMultipliers, scaleStartDate } = this.state;
    if (intervalMarkers.length === 0) {
      return null;
    }

    const slidesPositioned = slides.map(slide => {
      const matchingMarker = intervalMarkers.find(marker => isEqual(marker.date, slide.date));
      const startOffsetDays = differenceInCalendarDays(slide.date, scaleStartDate);
      const left = matchingMarker ? matchingMarker.left : startOffsetDays * scaleMultipliers.day;
      return { ...slide, left };
    });

    // Annotate duplicate dates for stacking
    slidesPositioned.forEach((slide, i) => {
      let level = 0;
      const prev = slidesPositioned[i - 1];
      if (prev && isEqual(prev.date, slide.date)) {
        level = prev.level + 1;
      }
      slide.level = level;
    });

    const renderIcon = category => {
      const { icon: Icon, fill } = categoryKey[category || 'default'];
      return <Icon fill={fill} />;
    };

    return (
      <div className="timeline-controller-slidemarkers">
        {slidesPositioned.map(({ left, level, category }, i) => (
          <div key={i} className="slidemarker" style={{ transform: `translate(${left}px, ${11 * level}px)` }}>
            {renderIcon(category)}
          </div>
        ))}
      </div>
    );
  }

  render () {
    const { containerWidth, breakpoint } = this.props;
    const {
      barPosition: { x },
      scaleWidth,
      smallInterval,
      intervalMarkers,
      barLeftOffset,
      transitioning,
      scrollHintVisible,
    } = this.state;
    const dragBounds = {
      left: -(scaleWidth - containerWidth / 2 + barLeftOffset),
      right: containerWidth / 2 - barLeftOffset,
    };
    const loupeProps = { breakpoint, smallInterval, intervalMarkers, barCenter: containerWidth / 2 - x - barLeftOffset };
    const sliderClasses = classNames({
      transitioning,
      'timeline-controller-slider': true,
    });
    const shouldRenderSlideMarkers = breakpoint.greaterThanOrEqual.lg;

    // TODO: i18n hardcoded string
    return (
      <div className="timeline-controller">
        <div className="timeline-controller-slider-container">
          <Draggable
            axis="x"
            defaultPosition={{x: containerWidth / 2, y: 0}}
            position={this.state.barPosition}
            bounds={dragBounds}
            onStart={this.handleDragStart}
            onDrag={this.handleDrag}
            onStop={this.handleDragStop}>

            <div className={sliderClasses} style={{ width: `${scaleWidth + barLeftOffset}px`, paddingLeft: barLeftOffset }}>
              <div
                className="timeline-controller-slider-hint"
                style={{ width: barLeftOffset, opacity: scrollHintVisible ? 1 : 0 }}
              >
                <div className="d-flex justify-content-center align-items-center">
                  <span>{breakpoint.lessThan.lg ? 'Drag' : 'Drag bar to scroll'}</span>
                </div>
              </div>
              {this.renderAxis()}
              {shouldRenderSlideMarkers ? this.renderSlideMarkers() : null}
            </div>
          </Draggable>
        </div>
        <div className="timeline-controller-gradient" />
        <ControllerLoupe {...loupeProps} />
        <div className="timeline-controller-hint d-none d-lg-block">Drag to Scroll</div>
      </div>
    );
  }
}

Controller.propTypes = {
  titleId: PropTypes.string.isRequired,
  containerWidth: PropTypes.number,
  markerLeftOffset: PropTypes.number,
  baseIntervalWidth: PropTypes.number,
};

Controller.defaultProps = {
  markerLeftOffset: -2, // Half the width of the interval marker icon
  baseIntervalWidth: 80, // Pixel width of major axis intervals
};

const mapStateToProps = ({ breakpoint, titles }, ownProps) => ({
  breakpoint,
  ...titles[ownProps.titleId],
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ handleControllerNavigation }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Controller);



// WEBPACK FOOTER //
// ./src/components/Timeline/Controller.js